import React, { useRef } from "react";

import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface DialogDeleteProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  title: string;
  description: string;
  note?: string;
  confirmButtonColor?: string;
  isDeleting?: boolean;
}

const DialogDelete: React.FC<DialogDeleteProps> = ({
  isOpen,
  onClose,
  onDelete,
  title,
  description,
  note,
  confirmButtonColor = "#F87171",
  isDeleting,
}) => {
  const dialogRef = useRef(null);
  const { t } = useTranslation("common");

  return (
    <Dialog open={isOpen}>
      <DialogContent onClick={() => onClose()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
          {note ? (
            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
              {note}
            </p>
          ) : null}
        </DialogHeader>
        <DialogFooter>
          <DialogClose onClick={() => onClose()} ref={dialogRef}>
            <Button variant="ghost">{t("cancel")}</Button>
          </DialogClose>
          <DialogClose>
            <Button
              className={`w-full ${confirmButtonColor} hover:bg-opacity-80`}
              onClick={onDelete}
              variant="destructive"
              type="button"
              data-cy="confirm-delete"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="animate-spin" />
                  {t("is_deleting")}
                </>
              ) : (
                <>{t("confirm_delete")}</>
              )}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogDelete;
